(() => {
  'use strict';
  const root = document.querySelector('[data-toasty-quiz]');
  if (!root || root.dataset.quizSharingReady) return;
  const config = root.querySelector('[data-quiz-sharing-copy]');
  if (!config) return;
  const copy = config.dataset;
  root.dataset.quizSharingReady = 'true';
  const characters = new Set(['starter', 'herb', 'milk', 'burnt', 'mg']);
  const states = new Map();
  const android = /Android/i.test(navigator.userAgent || '');
  const quizLink = () => `${location.origin}${location.pathname}?start=1`;
  const message = (result, text) => { result.querySelector('[data-quiz-sharing-status]').textContent = text || ''; };
  const fallback = (result, text, openImage = false) => {
    message(result, text);
    if (openImage) window.open(result.dataset.quizStorySrc, '_blank', 'noopener');
  };
  const png = async (url, signal) => {
    const response = await fetch(url, {signal, credentials: 'omit', headers: {Accept: 'image/png'}});
    if (!response.ok) throw new Error('Image unavailable');
    const blob = await response.blob();
    if (blob.type.split(';', 1)[0].toLowerCase() !== 'image/png') throw new Error('Invalid image type');
    const bytes = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
    if (![137, 80, 78, 71, 13, 10, 26, 10].every((value, index) => bytes[index] === value)) throw new Error('Invalid image');
    return blob;
  };
  const prepare = (result, state) => {
    if (state.loading || state.loaded) return;
    state.loading = true;
    const share = result.querySelector('[data-quiz-share]');
    const saveAction = result.querySelector('[data-quiz-save]');
    share.disabled = true;
    share.setAttribute('aria-busy', 'true');
    saveAction.setAttribute('aria-busy', 'true');
    if (!result.hidden) message(result, copy.loading);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    const character = result.dataset.quizResult;
    const ready = () => {
      share.disabled = state.sharing;
      share.removeAttribute('aria-busy');
      if (!result.hidden && result.querySelector('[data-quiz-sharing-status]').textContent === copy.loading) message(result, '');
    };
    const story = png(result.dataset.quizStorySrc, controller.signal).then(blob => {
      state.story = new File([blob], `toasty-${character}-story.png`, {type: 'image/png'});
      ready();
    });
    const save = png(result.dataset.quizSaveSrc, controller.signal).then(blob => {
      state.saveFile = new File([blob], `toasty-${character}-result.png`, {type: 'image/png'});
      state.saveURL = URL.createObjectURL(blob);
      saveAction.href = android ? result.dataset.quizSaveSrc : state.saveURL;
      if (android) saveAction.removeAttribute('download');
      saveAction.removeAttribute('aria-busy');
    });
    Promise.allSettled([story, save]).then(() => {
      clearTimeout(timeout);
      state.loading = false;
      state.loaded = true;
      saveAction.removeAttribute('aria-busy');
      if (!state.story) ready();
    });
  };
  const shown = (character, run) => {
    if (!characters.has(character)) return;
    const result = [...root.querySelectorAll('[data-quiz-result]')].find(node => node.dataset.quizResult === character);
    if (!result || result.hidden) return;
    let state = states.get(character);
    if (!state) { state = {loaded: false, loading: false, sharing: false, run: null}; states.set(character, state); }
    if (state.run !== run) {
      state.run = run;
      message(result, '');
      result.querySelector('[data-quiz-copy-field]').hidden = true;
    }
    prepare(result, state);
  };
  root.addEventListener('toasty:quiz-result', event => shown(event.detail?.character, event.detail?.run));
  root.addEventListener('click', event => {
    const action = event.target.closest('[data-quiz-share],[data-quiz-save],[data-quiz-copy-link]');
    const result = action?.closest('[data-quiz-result]');
    if (!result || result.hidden || !characters.has(result.dataset.quizResult)) return;
    const state = states.get(result.dataset.quizResult);
    if (action.hasAttribute('data-quiz-save')) {
      if (state?.saving) { event.preventDefault(); return; }
      if (!state?.saveURL) {
        if (state?.loading) event.preventDefault();
        fallback(result, state?.loading ? copy.saveNotReady : copy.saveError);
        return;
      }
      if (android && state.saveFile) {
        const data = {files: [state.saveFile]};
        try {
          if (typeof navigator.canShare === 'function' && typeof navigator.share === 'function' && navigator.canShare(data)) {
            // Use the cached tight card in this click's user activation, without a blob URL handoff.
            const promise = navigator.share(data);
            event.toastyQuizSaveRequested = true;
            event.preventDefault();
            state.saving = true;
            message(result, '');
            Promise.resolve(promise).catch(error => {
              if (error?.name !== 'AbortError' && !result.hidden) message(result, copy.saveNativeError);
            }).finally(() => { state.saving = false; });
            return;
          }
        } catch (_) { /* The direct tight image link remains usable. */ }
      }
      // Keep the native download/open action synchronous with the user's click.
      fallback(result, copy.saveNotice);
      return;
    }
    if (action.hasAttribute('data-quiz-copy-link')) {
      const manual = () => {
        const field = result.querySelector('[data-quiz-copy-field]');
        field.hidden = false; field.value = quizLink(); field.focus(); field.select();
        message(result, copy.copyManual);
      };
      if (!navigator.clipboard?.writeText) { manual(); return; }
      try { navigator.clipboard.writeText(quizLink()).then(() => message(result, copy.copySuccess), manual); } catch (_) { manual(); }
      return;
    }
    if (state?.sharing) return;
    if (!state?.story) { fallback(result, state?.loading ? copy.notReady : copy.error, !state?.loading); return; }
    const data = {files: [state.story]};
    try {
      if (typeof navigator.canShare !== 'function' || typeof navigator.share !== 'function' || !navigator.canShare(data)) {
        fallback(result, copy.unsupported, true); return;
      }
      // The File is already cached. Do not await fetch or any other work before share().
      const promise = navigator.share(data);
      state.sharing = true;
      action.disabled = true;
      message(result, '');
      Promise.resolve(promise).catch(error => {
        if (error?.name !== 'AbortError' && !result.hidden) fallback(result, copy.error);
      }).finally(() => { state.sharing = false; action.disabled = false; });
    } catch (error) {
      if (error?.name !== 'AbortError') fallback(result, copy.error);
    }
  });
  window.addEventListener('pagehide', event => {
    if (!event.persisted) states.forEach(state => { if (state.saveURL) URL.revokeObjectURL(state.saveURL); });
  });
  const current = [...root.querySelectorAll('[data-quiz-result]')].find(result => !result.hidden);
  if (current) shown(current.dataset.quizResult, 0);
})();
